import styled from "styled-components";

export type PopoverPlacement = "top" | "bottom" | "left" | "right";
/** Modal 등 차단형 오버레이의 elevation 층 — Modal `level` prop과 동일한 값. */
export type OverlayLevel = "media" | "low" | "default" | "high" | "top";

export type FloatingPopoverPlacement =
  | "top-start"
  | "top"
  | "top-end"
  | "bottom-start"
  | "bottom"
  | "bottom-end"
  | "left-start"
  | "left"
  | "left-end"
  | "right-start"
  | "right"
  | "right-end";

export type FloatingPopoverRect = Pick<
  DOMRectReadOnly,
  "top" | "right" | "bottom" | "left" | "width" | "height"
>;

export type FloatingPopoverSize = {
  width: number;
  height: number;
};

export type FloatingPopoverViewport = {
  width: number;
  height: number;
};

export type FloatingPopoverPosition = {
  left: number;
  top: number;
  placement: FloatingPopoverPlacement;
  /** 회피 영역과 겹치지 않는 자리를 찾지 못했다. */
  hidden?: boolean;
};

export type ResolveFloatingPopoverPositionOptions = {
  anchorRect: FloatingPopoverRect;
  popoverSize: FloatingPopoverSize;
  viewport: FloatingPopoverViewport;
  placement?: FloatingPopoverPlacement;
  offset?: number;
  margin?: number;
  /** 명시한 경계 안에서 배치한다. 생략하면 viewport를 사용한다. */
  boundary?: FloatingPopoverRect;
  /** 기본 방향이 맞지 않을 때 순서대로 검사할 추가 방향. 생략 시 기존 축별 flip. */
  fallbackPlacements?: readonly FloatingPopoverPlacement[];
  /** 우선순위가 높은 레이어. margin만큼 간격을 두고 회피한다. */
  avoidRects?: readonly FloatingPopoverRect[];
};

// 전역 elevation 사다리 — c6 차단형 오버레이(Modal)의 z 순서. 일반 Modal 4층은 앱이 화면 의미를
// 매핑하고, media=950은 MediaOverlay가 legacy fullscreen(950) < IO lock(low=1000) 순서를 보존하는 전용 하단층.
// low 이후 층 사이 100 간격은 **의도적** — consumer가 자기 레이어(예: sigmakit cover)를 그 사이 값(1150 등)으로 끼움.
// media와 low 사이는 기존 fullscreen/IO 안전 순서를 복원하는 50 간격이다.
// 포털형 오버레이(dropdown/tooltip/anchored popup)는 이 사다리 밖 — inlineOverlayZIndex 사용(모달 밴드 아래).
export const overlayZIndex = {
  media: 950,
  low: 1000,
  default: 1100,
  high: 1200,
  top: 1300,
  // toast = c6 ToastHost가 쓰는 최상위 알림 레이어. 모든 모달(top=1300) 위에 떠 모달 inert에 안 잡힌다.
  // 사다리 천장과의 큰 간격은 consumer가 자기 레이어(또는 잔존 react-toastify=9999)를 그 아래로 끼울 여지.
  toast: 9999,
} satisfies Record<OverlayLevel | "toast", number>;

// 포털형 오버레이의 z.
// - popover(AnchoredPopup): 자기 컨테이너/페이지 chrome만 넘으면 되는 낮은 값.
// - tooltip: portal + position:fixed로 body(모달 밖) 또는 모달 Dialog(모달 안)에 그린다. 모달 밖에선
//   트리거가 속한 stacking context를 벗어나므로, 페이지 chrome(sticky header·cover 등 z>popover)을 넘도록
//   최하단 media 밴드(overlayZIndex.media=950) 아래에 둔다 — 잔존 페이지 tooltip이 차단형 overlay를 덮지 않게.
//   (모달 안 tooltip은 Dialog에 mount돼 이 값이 Dialog stacking 안에 스코프된다.) tooltip > popover(겹칠 때 우선).
export const inlineOverlayZIndex = {
  popover: 20,
  tooltip: 900,
} as const;

// dismissable 오버레이가 자기 portal root에 다는 generic 마커. 모달 Backdrop 안에 있으면(=모달 위에 떠
// 자기 dismiss를 처리하는 레이어) 모달은 그 제스처를 양보. Modal이 특정 컴포넌트에 결합하지 않기 위한 contract.
// 값 = 이 레이어가 실제로 소비하는 제스처 토큰(공백 구분: "escape" / "pointer"). Modal 실드는 해당 제스처를
// 소비하는 레이어가 있을 때만 양보한다 — opt-out popup(closeOnEscape=false 등)은 그 토큰이 없어 모달이 직접
// 처리(아무것도 안 닫는 dead gesture 방지). 내부 c6 Modal↔AnchoredPopup 계약이라 legacy presence 하위호환은
// 두지 않는다(소비처 실사용 0 확인).
export const ESCAPE_LAYER_ATTR = "data-c6-escape-layer";

export type EscapeLayerGesture = "escape" | "pointer";

/** 레이어가 소비하는 제스처 토큰 문자열. 아무것도 소비 안 하면 "" (마커 제거 신호). */
export function escapeLayerValue(consumes: { escape: boolean; pointer: boolean }): string {
  const tokens: EscapeLayerGesture[] = [];
  if (consumes.escape) tokens.push("escape");
  if (consumes.pointer) tokens.push("pointer");
  return tokens.join(" ");
}

/** 특정 제스처를 소비하는 escape-layer selector (`~=` = 공백 구분 토큰 매칭). */
export function escapeLayerSelector(gesture: EscapeLayerGesture): string {
  return `[${ESCAPE_LAYER_ATTR}~="${gesture}"]`;
}

export function resolveFloatingPopoverPosition({
  anchorRect,
  popoverSize,
  viewport,
  placement = "bottom-end",
  offset = 6,
  margin = 8,
  boundary,
  fallbackPlacements,
  avoidRects,
}: ResolveFloatingPopoverPositionOptions): FloatingPopoverPosition {
  if (boundary) {
    const result = resolveFloatingPopoverPosition({
      anchorRect: {
        width: anchorRect.width,
        height: anchorRect.height,
        left: anchorRect.left - boundary.left,
        right: anchorRect.right - boundary.left,
        top: anchorRect.top - boundary.top,
        bottom: anchorRect.bottom - boundary.top,
      },
      popoverSize,
      viewport: { width: boundary.width, height: boundary.height },
      placement,
      offset,
      margin,
      fallbackPlacements,
      avoidRects: avoidRects?.map((rect) => ({
        ...rect,
        left: rect.left - boundary.left,
        right: rect.right - boundary.left,
        top: rect.top - boundary.top,
        bottom: rect.bottom - boundary.top,
      })),
    });
    return { ...result, left: result.left + boundary.left, top: result.top + boundary.top };
  }
  const blocked = ({ left, top }: { left: number; top: number }) =>
    avoidRects?.some(
      (rect) =>
        left < rect.right + margin &&
        left + popoverSize.width > rect.left - margin &&
        top < rect.bottom + margin &&
        top + popoverSize.height > rect.top - margin,
    ) ?? false;
  if (fallbackPlacements || avoidRects?.length) {
    for (const candidate of [placement, ...(fallbackPlacements ?? [])]) {
      const base = createFloatingPopoverPosition({
        anchorRect,
        popoverSize,
        placement: candidate,
        offset,
      });
      // 교차 축은 경계 안으로 정렬할 수 있지만 주 축은 Item 바깥 공간이 있어야 한다.
      const horizontal = candidate.startsWith("left") || candidate.startsWith("right");
      const fits = horizontal
        ? base.left >= margin &&
          base.left + popoverSize.width <= viewport.width - margin &&
          popoverSize.height <= viewport.height - 2 * margin
        : base.top >= margin &&
          base.top + popoverSize.height <= viewport.height - margin &&
          popoverSize.width <= viewport.width - 2 * margin;
      const position = {
        left: clampToViewport(base.left, popoverSize.width, viewport.width, margin),
        top: clampToViewport(base.top, popoverSize.height, viewport.height, margin),
        placement: candidate,
      };
      if (fits && !blocked(position)) return position;
    }
  }
  const resolvedPlacement = resolveFloatingPlacement({
    anchorRect,
    popoverSize,
    viewport,
    placement,
    offset,
    margin,
  });
  const basePosition = createFloatingPopoverPosition({
    anchorRect,
    popoverSize,
    placement: resolvedPlacement,
    offset,
  });

  const result = {
    left: clampToViewport(basePosition.left, popoverSize.width, viewport.width, margin),
    top: clampToViewport(basePosition.top, popoverSize.height, viewport.height, margin),
    placement: resolvedPlacement,
  };
  return blocked(result) ? { ...result, hidden: true } : result;
}

function resolveFloatingPlacement({
  anchorRect,
  popoverSize,
  viewport,
  placement,
  offset,
  margin,
}: Required<
  Pick<
    ResolveFloatingPopoverPositionOptions,
    "anchorRect" | "popoverSize" | "viewport" | "placement" | "offset" | "margin"
  >
>): FloatingPopoverPlacement {
  const basePosition = createFloatingPopoverPosition({
    anchorRect,
    popoverSize,
    placement,
    offset,
  });
  const bottomLimit = viewport.height - margin;
  const topLimit = margin;
  const spaceAbove = anchorRect.top - margin;
  const spaceBelow = viewport.height - margin - anchorRect.bottom;

  if (
    placement.startsWith("bottom") &&
    basePosition.top + popoverSize.height > bottomLimit &&
    spaceAbove > spaceBelow
  ) {
    return flipVerticalPlacement(placement);
  }

  if (placement.startsWith("top") && basePosition.top < topLimit && spaceBelow > spaceAbove) {
    return flipVerticalPlacement(placement);
  }

  const rightLimit = viewport.width - margin;
  const leftLimit = margin;
  const spaceLeft = anchorRect.left - margin;
  const spaceRight = viewport.width - margin - anchorRect.right;

  if (
    placement.startsWith("right") &&
    basePosition.left + popoverSize.width > rightLimit &&
    spaceLeft > spaceRight
  ) {
    return flipHorizontalPlacement(placement);
  }

  if (placement.startsWith("left") && basePosition.left < leftLimit && spaceRight > spaceLeft) {
    return flipHorizontalPlacement(placement);
  }

  return placement;
}

function flipVerticalPlacement(placement: FloatingPopoverPlacement): FloatingPopoverPlacement {
  if (placement.startsWith("bottom")) {
    return placement.replace("bottom", "top") as FloatingPopoverPlacement;
  }

  if (placement.startsWith("top")) {
    return placement.replace("top", "bottom") as FloatingPopoverPlacement;
  }

  return placement;
}

function flipHorizontalPlacement(placement: FloatingPopoverPlacement): FloatingPopoverPlacement {
  if (placement.startsWith("right")) {
    return placement.replace("right", "left") as FloatingPopoverPlacement;
  }

  if (placement.startsWith("left")) {
    return placement.replace("left", "right") as FloatingPopoverPlacement;
  }

  return placement;
}

function createFloatingPopoverPosition({
  anchorRect,
  popoverSize,
  placement,
  offset,
}: {
  anchorRect: FloatingPopoverRect;
  popoverSize: FloatingPopoverSize;
  placement: FloatingPopoverPlacement;
  offset: number;
}): Omit<FloatingPopoverPosition, "placement"> {
  if (placement.startsWith("left") || placement.startsWith("right")) {
    const left = placement.startsWith("left")
      ? anchorRect.left - popoverSize.width - offset
      : anchorRect.right + offset;

    if (placement.endsWith("start")) {
      return { left, top: anchorRect.top };
    }

    if (placement.endsWith("end")) {
      return { left, top: anchorRect.bottom - popoverSize.height };
    }

    return { left, top: anchorRect.top + anchorRect.height / 2 - popoverSize.height / 2 };
  }

  const top = placement.startsWith("top")
    ? anchorRect.top - popoverSize.height - offset
    : anchorRect.bottom + offset;

  if (placement.endsWith("start")) {
    return { left: anchorRect.left, top };
  }

  if (placement.endsWith("end")) {
    return { left: anchorRect.right - popoverSize.width, top };
  }

  return { left: anchorRect.left + anchorRect.width / 2 - popoverSize.width / 2, top };
}

function clampToViewport(
  value: number,
  size: number,
  viewportSize: number,
  margin: number,
): number {
  const min = margin;
  const max = viewportSize - margin - size;
  if (max < min) {
    return min;
  }
  return Math.min(Math.max(value, min), max);
}

// inline 오버레이 표면(dropdown 메뉴/color picker) — 자기 컨테이너 stacking에 스코프된 고정 로컬 z.
export const PopoverSurface = styled.div`
  position: absolute;
  z-index: ${inlineOverlayZIndex.popover};
`;
